import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Select, DatePicker, Form, Segmented } from 'antd';
import { CalendarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { taskService } from '../services/taskService';
import { projectService, Project } from '../services/projectService';
import { PersonAvatar } from './ui';
import './AssignmentForm.css';

const { RangePicker } = DatePicker;

type DateMode = 'custom' | 'range';

interface AssignmentFormProps {
  visible: boolean;
  onClose: () => void;
  employeeId: string | number;
  /** Shown at the top so it's clear who is being assigned. */
  employeeName?: { name: string; surname: string; imageUrl?: string };
  /** Pre-selected day (YYYY-MM-DD), e.g. the day picked on the calendar. */
  defaultDate?: string | null;
  /** Month the pickers open on. */
  month?: Dayjs;
  /** Called with the first assigned day, so the page can jump to its month. */
  onAssigned: (firstDate?: string) => void;
}

interface FormValues {
  project: number;
  customDates?: Dayjs[];
  dates?: [Dayjs, Dayjs];
}

const AssignmentForm: React.FC<AssignmentFormProps> = ({
  visible,
  onClose,
  employeeId,
  employeeName,
  defaultDate,
  month,
  onAssigned,
}) => {
  const { t, i18n } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [saving, setSaving] = useState(false);
  const [dateMode, setDateMode] = useState<DateMode>('custom');
  const [form] = Form.useForm<FormValues>();

  const customDates: Dayjs[] | undefined = Form.useWatch('customDates', form);
  const range: [Dayjs, Dayjs] | undefined = Form.useWatch('dates', form);

  useEffect(() => {
    if (!visible) return;
    // Start clean every time, with the day that was selected on the calendar (if any)
    form.resetFields();
    setDateMode('custom');
    if (defaultDate) form.setFieldsValue({ customDates: [dayjs(defaultDate)] });
    projectService
      .getProjects()
      .then(setProjects)
      .catch(() => toast.error(t('projects.fetchError')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const projectOptions = useMemo(
    () =>
      projects
        .filter((project) => project.status === 'active')
        .map((project) => ({ value: project.id, label: project.name })),
    [projects],
  );

  const formatDay = (date: Dayjs) =>
    date.toDate().toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });

  let summary: string | null = null;
  if (dateMode === 'custom' && customDates?.length) {
    summary = t('assignment.daysSelected', { count: customDates.length });
  } else if (dateMode === 'range' && range?.[0] && range?.[1]) {
    summary = t('assignment.rangeSummary', {
      count: range[1].startOf('day').diff(range[0].startOf('day'), 'day') + 1,
      from: formatDay(range[0]),
      to: formatDay(range[1]),
    });
  }

  const handleFinish = async (values: FormValues) => {
    // Build the payload from the visible mode only — antd keeps the hidden field's value around
    const payload: Record<string, unknown> = { employee_id: employeeId };
    let firstDate: string | undefined;
    if (dateMode === 'range' && values.dates) {
      payload.start_date = values.dates[0].format('YYYY-MM-DD');
      payload.end_date = values.dates[1].format('YYYY-MM-DD');
      firstDate = payload.start_date as string;
    } else if (values.customDates?.length) {
      const sorted = [...values.customDates].sort((a, b) => a.valueOf() - b.valueOf());
      payload.dates = sorted.map((date) => date.format('YYYY-MM-DD'));
      firstDate = (payload.dates as string[])[0];
    }

    setSaving(true);
    try {
      await taskService.assignEmployeeCalendar(String(values.project), payload);
      toast.success(t('assignment.success'));
      onAssigned(firstDate);
      onClose();
    } catch (e: any) {
      const errors = e?.response?.data?.errors as Record<string, string[]> | undefined;
      const firstError = errors ? Object.values(errors)[0]?.[0] : undefined;
      toast.error(firstError || e?.response?.data?.message || t('assignment.failure'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={() => !saving && onClose()}
      title={t('assignment.title')}
      width={480}
      footer={
        <div className="asg-footer">
          <Button onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" loading={saving} onClick={() => form.submit()}>
            {t('assignment.assign')}
          </Button>
        </div>
      }
    >
      {employeeName ? (
        <div className="asg-subject">
          <PersonAvatar
            name={employeeName.name}
            surname={employeeName.surname}
            imageUrl={employeeName.imageUrl}
            size={40}
          />
          <div className="e22-row-body">
            <div className="asg-subject-label">{t('assignment.employee')}</div>
            <div className="e22-row-title">
              {employeeName.name} {employeeName.surname}
            </div>
          </div>
        </div>
      ) : null}

      <Form form={form} layout="vertical" requiredMark={false} onFinish={handleFinish}>
        <Form.Item
          name="project"
          label={t('assignment.project')}
          extra={
            <span className="asg-extra">
              <InfoCircleOutlined /> {t('assignment.activeProjectsHint')}
            </span>
          }
          rules={[{ required: true, message: t('assignment.validation.project') }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t('assignment.selectProject')}
            options={projectOptions}
          />
        </Form.Item>

        <Form.Item label={t('assignment.dateSelection')}>
          <Segmented<DateMode>
            block
            value={dateMode}
            onChange={setDateMode}
            options={[
              { value: 'custom', label: t('assignment.singleDays') },
              { value: 'range', label: t('assignment.dateRange') },
            ]}
          />
        </Form.Item>

        {dateMode === 'custom' ? (
          <Form.Item
            name="customDates"
            label={t('assignment.selectDates')}
            extra={t('assignment.pickDaysHint')}
            rules={[{ required: true, message: t('assignment.validation.date') }]}
          >
            <DatePicker
              multiple
              maxTagCount="responsive"
              format="DD.MM.YYYY"
              defaultPickerValue={month}
              style={{ width: '100%' }}
            />
          </Form.Item>
        ) : (
          <Form.Item
            name="dates"
            label={t('assignment.dateRange')}
            rules={[{ required: true, message: t('assignment.validation.dateRange') }]}
          >
            <RangePicker format="DD.MM.YYYY" defaultPickerValue={month ? [month, month] : undefined} style={{ width: '100%' }} />
          </Form.Item>
        )}

        {summary ? (
          <div className="asg-summary">
            <CalendarOutlined />
            <span>{summary}</span>
          </div>
        ) : null}
      </Form>
    </Modal>
  );
};

export default AssignmentForm;
